/**
 * TopHeader - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * Premium glass effect with lime accents
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, FileText } from 'lucide-react';

interface TopHeaderProps {
  onHelpClick?: () => void;
}

export const TopHeader: React.FC<TopHeaderProps> = ({ onHelpClick }) => {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-50 bg-white/5 backdrop-blur-xl border-b border-white/10 shadow-lg">
      <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
        {/* Logo - Matching Login v2 */}
        <div
          className="flex items-center gap-3 opacity-0 animate-slide-in"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-400 to-lime-500 flex items-center justify-center shadow-lg shadow-lime-500/30">
            <FileText size={24} strokeWidth={2.5} className="text-teal-700" />
          </div>
          <div>
            <span className="text-white text-xl font-bold tracking-tight">FacturXPro</span>
            <p className="text-white/50 text-xs">{t('onboarding.topHeader.initialSetup')}</p>
          </div>
        </div>

        {/* Help link - Glass effect */}
        <button
          onClick={onHelpClick}
          className="flex items-center gap-2 text-sm text-white/70 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-2xl hover:text-lime-400 hover:border-lime-400/50 hover:bg-white/15 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-lime-500/10 group"
        >
          <HelpCircle className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
          <span className="font-medium">{t('onboarding.topHeader.needHelp')}</span>
        </button>
      </div>

      {/* Custom animation */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-slide-in {
          animation: slide-in 0.6s ease-out;
        }
      `}</style>
    </header>
  );
};
